/**
 * EpisodioModal — o detalhe de UMA ocorrência.
 *
 * A lista responde "o que aconteceu"; esta janela responde "e aí?". As três
 * perguntas que ela existe para fechar, na ordem em que alguém as faz às 3h
 * da manhã:
 *
 *   1. o que exatamente disparou, e quando;
 *   2. alguém foi avisado? por onde? o telefone chegou a tocar?
 *   3. o que foi feito a respeito — e isso não está em lugar nenhum do
 *      sistema até alguém escrever aqui.
 *
 * Por isso as ligações aparecem ao lado dos eventos. Elas não moram em
 * `alert_events` (o canal de voz tem estado próprio), e sem elas a linha do
 * tempo diria "notificado" sem contar que o telefone tocou seis vezes e
 * ninguém atendeu — que é justamente a informação que muda a decisão.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCheck,
  CircleSlash,
  MessageSquare,
  Phone,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";

import { alertEventsApi, episodiosApi } from "@/api/alerts";
import { apiErrorMessage } from "@/api/client";
import { avisarMudanca } from "@/lib/sincronia";
import { useAuth } from "@/contexts/AuthContext";
import { useEpisodio } from "@/hooks/queries";
import { RULE_KIND_LABELS } from "@/lib/ruleKinds";
import { detalhesDoAlerta } from "@/lib/detalhesDoAlerta";
import { formatDate, timeAgo } from "@/lib/format";
import type { EpisodioStatus, EventoDoEpisodio, RuleKind, Severity } from "@/types";

import { Badge } from "@/components/Badge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/Button";
import { Loading } from "@/components/Loading";
import { Modal } from "@/components/Modal";
import { SEVERIDADE_LABEL, SEVERIDADE_TOM } from "@/lib/severidade";





const STATUS_LABEL: Record<EpisodioStatus, string> = {
  aberto: "Em aberto",
  reconhecido: "Reconhecido",
  resolvido: "Resolvido",
};

const STATUS_TOM: Record<EpisodioStatus, "danger" | "warning" | "success"> = {
  aberto: "danger",
  reconhecido: "warning",
  resolvido: "success",
};

const EVENTO_LABEL: Record<string, string> = {
  fired: "Disparou",
  // Sem esta linha o tipo aparece cru ("escalated") na linha do tempo — em
  // inglês, no meio de uma tela em português, justamente no evento que conta
  // a pior notícia do episódio.
  escalated: "Agravou",
  renotified: "Lembrete",
  notification_sent: "Notificou",
  notification_failed: "Falha ao notificar",
  acknowledged: "Reconhecido",
  recovered: "Normalizou",
  auto_closed: "Fechado automaticamente",
  manually_closed: "Fechado manualmente",
};

/** Ligação: o que o operador precisa entender sem aprender nosso vocabulário. */
const CHAMADA_LABEL: Record<string, string> = {
  discando: "chamando",
  reconhecida: "atendida, reconheceu",
  sem_resposta: "ninguém reconheceu",
  falhou: "não completou",
};

/**
 * Tom por desfecho. Ligação atendida em que ninguém apertou 1 é `sem_resposta`
 * — e precisa ficar visualmente distinta de "atendida, reconheceu", porque é
 * exatamente a confusão que faz alguém achar que o alerta foi tratado.
 */
const CHAMADA_TOM: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  discando: "neutral",
  reconhecida: "success",
  sem_resposta: "warning",
  falhou: "danger",
};

function Linha({ e }: { e: EventoDoEpisodio }) {
  const canal = typeof e.payload?.channel_name === "string" ? e.payload.channel_name : null;
  return (
    <li className="relative pl-6 pb-4 last:pb-0">
      <span
        className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600"
        aria-hidden
      />
      <span className="absolute left-[3px] top-4 bottom-0 w-px bg-slate-200 dark:bg-slate-800 last:hidden" />
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
          {EVENTO_LABEL[e.event_type] ?? e.event_type}
        </span>
        {canal && (
          <span className="text-xs text-slate-500 dark:text-slate-400">via {canal}</span>
        )}
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {formatDate(e.created_at)}
        </span>
      </div>
      {e.message && (
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{e.message}</p>
      )}
    </li>
  );
}

export function EpisodioModal({
  episodioId,
  onClose,
}: {
  episodioId: string | null;
  onClose: () => void;
}) {
  const auth = useAuth();
  const qc = useQueryClient();
  const detalhe = useEpisodio(episodioId);
  const [texto, setTexto] = useState("");
  const [confirmarFechar, setConfirmarFechar] = useState(false);

  const ep = detalhe.data?.episodio;
  // O retrato que o motor usou pra decidir é o do DISPARO — os lembretes
  // repetem o mesmo veredito, e o que importa é o que levou ao alerta.
  const disparo = detalhe.data?.eventos.find((e) => e.event_type === "fired");
  const detalhes = detalhesDoAlerta(ep?.rule_kind, disparo?.payload);

  /**
   * Recarrega o detalhe e a lista depois de agir sobre a ocorrência.
   *
   * `refetchQueries` e não `invalidateQueries`: invalidar só MARCA como
   * velho e deixa a recarga pra quando a consulta for observada de novo.
   * Aqui a lista está montada atrás do modal e o detalhe é o que a pessoa
   * está olhando — reconhecer um alerta e o status continuar como estava,
   * por causa de uma marca que ninguém executou, é o tipo de detalhe que
   * faz o operador duvidar do botão e clicar de novo.
   *
   * O aviso entre abas vai junto porque ocorrência é a tela que mais fica
   * aberta em dois lugares ao mesmo tempo — o computador e o celular.
   */
  function recarregarOcorrencias() {
    void qc.refetchQueries({ queryKey: ["alert-episodio", episodioId] });
    void qc.refetchQueries({ queryKey: ["alert-episodios"] });
    void qc.refetchQueries({ queryKey: ["active-alerts"] });
    avisarMudanca([["alert-episodio", episodioId], ["alert-episodios"],
                   ["active-alerts"]]);
  }

  const comentarMut = useMutation({
    mutationFn: (t: string) => episodiosApi.comentar(episodioId!, t),
    onSuccess: () => {
      setTexto("");
      recarregarOcorrencias();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const ackMut = useMutation({
    mutationFn: () => alertEventsApi.acknowledge(ep!.rule_id!, ep!.equipment_id!),
    onSuccess: () => {
      toast.success("Alerta reconhecido");
      recarregarOcorrencias();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const fecharMut = useMutation({
    mutationFn: () => episodiosApi.encerrar(episodioId!),
    onSuccess: () => {
      toast.success("Ocorrência encerrada");
      // `recarregarOcorrencias` já cobre os alertas ativos; a linha extra
      // que existia aqui invalidava a mesma chave por outro caminho e era
      // a única do arquivo que ainda só marcava como velha.
      recarregarOcorrencias();
      setConfirmarFechar(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Reconhecer exige regra + ativo. A queda de site tem estado próprio e não
  // passa pelo mesmo caminho — oferecer o botão ali só produziria erro.
  const podeReconhecer =
    !!ep &&
    ep.status === "aberto" &&
    ep.escopo === "equipamento" &&
    !!ep.rule_id &&
    !!ep.equipment_id &&
    auth.hasMinRole("operator");

  // Encerrar vale também no que já foi reconhecido: reconhecer é "estou
  // ciente", encerrar é "acabou". A API aceita os dois estados.
  // Basta a ocorrência não estar encerrada. Não exige escopo de equipamento
  // nem alerta ativo — o endpoint encerra o EPISÓDIO, e o episódio zumbi (o
  // que ficou aberto porque o evento de fechamento nasceu sem episodio_id) é
  // justamente o que mais precisa deste botão.
  const podeEncerrar =
    !!ep && ep.status !== "resolvido" && auth.hasMinRole("operator");

  const podeComentar = auth.hasMinRole("operator");

  return (
    <Modal
      open={!!episodioId}
      onClose={onClose}
      title={ep ? (ep.equipment_name ?? ep.site_name ?? "Ocorrência") : "Ocorrência"}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {/* Ações do ALERTA à esquerda; sair da janela à direita.
              O botão da direita se chamava "Fechar" e parecia encerrar a
              ocorrência — ele só fecha o modal. "Voltar" não promete nada. */}
          <div className="flex items-center gap-2">
            {podeReconhecer && (
              <Button
                variant="secondary"
                icon={<CheckCheck className="h-4 w-4" />}
                loading={ackMut.isPending}
                onClick={() => ackMut.mutate()}
              >
                Reconhecer
              </Button>
            )}
            {podeEncerrar && (
              <Button
                variant="ghost"
                icon={<CircleSlash className="h-4 w-4" />}
                onClick={() => setConfirmarFechar(true)}
              >
                Encerrar
              </Button>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Voltar
          </Button>
        </div>
      }
    >
      {detalhe.isLoading && <Loading label="Carregando ocorrência..." />}

      {/* Sem este bloco, um erro na API renderiza um modal VAZIO — que foi
          exatamente o que aconteceu em 17/09 com um `:fim::timestamptz` mal
          interpretado pelo SQLAlchemy: 500 na rota, e a tela não dizia nada.
          Tela em branco é o pior estado possível: não informa e não sugere
          o que fazer. */}
      {detalhe.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm
                        text-red-800 dark:border-red-900/60 dark:bg-red-950/30
                        dark:text-red-200">
          <p className="font-medium">Não consegui carregar esta ocorrência.</p>
          <p className="mt-1 text-xs opacity-90">
            {apiErrorMessage(detalhe.error)}
          </p>
        </div>
      )}

      {!detalhe.isLoading && !detalhe.isError && !ep && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Ocorrência não encontrada. Ela pode ter sido apagada, ou pertencer a
          outro cliente.
        </p>
      )}

      {ep && (
        <div className="space-y-5">
          {/* Cabeçalho ------------------------------------------------- */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TOM[ep.status]}>{STATUS_LABEL[ep.status]}</Badge>
            {ep.severity && (
              <Badge tone={SEVERIDADE_TOM[ep.severity]}>
                {SEVERIDADE_LABEL[ep.severity]}
              </Badge>
            )}
            <Badge tone="brand">
              {ep.rule_kind
                ? RULE_KIND_LABELS[ep.rule_kind as RuleKind] ?? ep.rule_kind
                : "—"}
            </Badge>
            {ep.escopo === "site" && <Badge tone="neutral">site inteiro</Badge>}
          </div>

          {ep.mensagem && (
            <p className="text-sm text-slate-700 dark:text-slate-300">{ep.mensagem}</p>
          )}

          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400">Começou</dt>
              <dd className="text-slate-800 dark:text-slate-200">
                {formatDate(ep.inicio)}{" "}
                <span className="text-xs text-slate-400">({timeAgo(ep.inicio)})</span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400">
                {ep.fim ? "Normalizou" : "Situação"}
              </dt>
              <dd className="text-slate-800 dark:text-slate-200">
                {ep.fim ? (
                  <>
                    {formatDate(ep.fim)}
                    {ep.duracao_min != null && (
                      <span className="text-xs text-slate-400"> · durou {ep.duracao_min} min</span>
                    )}
                  </>
                ) : (
                  <span className="text-red-600 dark:text-red-400">ainda em aberto</span>
                )}
              </dd>
            </div>
            {ep.tenant_name && (
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Cliente</dt>
                <dd className="text-slate-800 dark:text-slate-200">{ep.tenant_name}</dd>
              </div>
            )}
            {ep.site_name && (
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Site</dt>
                <dd className="text-slate-800 dark:text-slate-200">{ep.site_name}</dd>
              </div>
            )}
            {detalhes.sensores.length > 0 && (
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">
                  {detalhes.sensores.length > 1 ? "Sensores" : "Sensor"}
                </dt>
                <dd className="text-slate-800 dark:text-slate-200">
                  {detalhes.sensores.join(", ")}
                </dd>
              </div>
            )}
            {ep.reconhecido_em && (
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Reconhecido</dt>
                <dd className="text-slate-800 dark:text-slate-200">
                  {ep.reconhecido_por ?? "—"}
                  {/* A distinção não é decorativa: ack por telefone prova que
                      alguém atendeu um número, não que a pessoa entrou no
                      sistema. Quem lê meses depois precisa saber qual foi. */}
                  {ep.reconhecido_via === "voice" && (
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                      — pelo telefone
                    </span>
                  )}
                </dd>
              </div>
            )}
          </dl>

          {/* O que o motor mediu. Sem isto a frase "Porta aberta há 5 min"
              não diz qual porta, nem a temperatura de qual sonda. */}
          {detalhes.linhas.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                No momento do disparo
              </h3>
              <dl className="grid gap-x-6 gap-y-1.5 rounded-lg border border-surface-200 p-3 text-sm
                             sm:grid-cols-2 dark:border-slate-800">
                {detalhes.linhas.map((l, i) => (
                  <div key={`${l.rotulo}-${i}`} className="flex items-baseline justify-between gap-3">
                    <dt className="text-xs text-slate-500 dark:text-slate-400">{l.rotulo}</dt>
                    <dd className="text-right font-medium text-slate-800 dark:text-slate-200">{l.valor}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Ligações -------------------------------------------------- */}
          {detalhe.data!.chamadas.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <Phone className="h-3.5 w-3.5" />
                Ligações ({detalhe.data!.chamadas.length})
              </h3>
              <ul className="divide-y divide-surface-100 rounded-lg border border-surface-200 dark:divide-slate-800 dark:border-slate-800">
                {detalhe.data!.chamadas.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                    {/* Nome do canal em cima, número embaixo: a pergunta das
                        3h é "quem foi acionado", e "Plantão frigorífico"
                        responde isso; o +55 sozinho, não. */}
                    <span className="min-w-0">
                      <span className="block truncate text-slate-800 dark:text-slate-200">
                        {c.canal_nome ?? "canal removido"}
                      </span>
                      <span className="block font-mono text-xs text-slate-500 dark:text-slate-400">
                        {c.para}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={CHAMADA_TOM[c.status] ?? "neutral"}>
                        {CHAMADA_LABEL[c.status] ?? c.status}
                      </Badge>
                      <span className="text-xs text-slate-400">{formatDate(c.iniciada_em)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Linha do tempo -------------------------------------------- */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              O que aconteceu
            </h3>
            <ul>
              {detalhe.data!.eventos.map((e) => (
                <Linha key={e.id} e={e} />
              ))}
            </ul>
          </section>

          {/* Comentários ----------------------------------------------- */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              <MessageSquare className="h-3.5 w-3.5" />
              Registro
            </h3>

            {detalhe.data!.comentarios.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Nada registrado ainda.
              </p>
            ) : (
              <ul className="space-y-3">
                {detalhe.data!.comentarios.map((c) => (
                  <li key={c.id} className="rounded-lg bg-surface-50/60 p-3 dark:bg-slate-900/50">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {c.autor_nome}
                      </span>
                      <span className="text-xs text-slate-400">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                      {c.texto}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            {podeComentar && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={3}
                  placeholder="O que foi feito? Ex.: disjuntor do circuito tinha desarmado, religado às 4h10."
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-600"
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    icon={<Send className="h-3.5 w-3.5" />}
                    loading={comentarMut.isPending}
                    disabled={texto.trim().length === 0}
                    onClick={() => comentarMut.mutate(texto.trim())}
                  >
                    Registrar
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
      <ConfirmDialog
        open={confirmarFechar}
        title="Encerrar esta ocorrência?"
        message={
          "O alerta sai da lista de ativos sem ter normalizado sozinho. " +
          "Use quando o problema foi tratado ou não vai se resolver — se a " +
          "condição continuar valendo, o motor abre uma ocorrência nova no " +
          "próximo ciclo."
        }
        confirmLabel="Encerrar"
        loading={fecharMut.isPending}
        onCancel={() => setConfirmarFechar(false)}
        onConfirm={() => fecharMut.mutate()}
      />
    </Modal>
  );
}
